import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { FormattedDate } from 'react-intl';
import { getPendingStatus } from '../../util/requestReducer';

import { TranslatedCopy } from '../../intl/TranslatedCopy';

import Address from '~COMPONENTS/address/Address';
import Button from '~COMPONENTS/button/Button';
import Currency from '../../components/Currency';
import { H2 } from '~COMPONENTS/containers/Headers';
/* eslint-disable import/no-cycle */
import Link from '../../components/Link';

import Sidebar from '~COMPONENTS/sidebar/Sidebar';
import { Spinner } from '~COMPONENTS/index';
import OrderDetailsSummaryBlock from './OrderDetailsSummaryBlock';

import { toTitleCase } from '../../util/toTitleCase';
import { ORDERTYPE_STRING_MAP } from '../constants';

import { requestSubmittedReturns } from '../../orders/orderReturns/actions';

export const OrderDetailsSummary = ({
  accountName,
  currencyCode,
  customerRequestDate,
  departmentName,
  handleBeginReturnClick,
  handleRelatedReturnsClick,
  hasReturns,
  isEligibleForReturn,
  isExpiredContractReturn,
  hasFutureContractReturn,
  futureReturnStartDate,
  netWholeSaleTotal,
  orderSubmittedBy,
  orderSubmittedDate,
  orderType,
  paymentMethod,
  poNumber,
  shipToAddress,
  totalQty,
  isForecast,
  isGreaterChinaSalesOrg,
  isAustraliaSalesOrg,
  isUSSalesOrg,
  isJapanSalesOrg,
  isLoadingSubmittedReturns
}) => {
  const methodOfPayment = isForecast && paymentMethod === 'COMP-CASH' ? 'NA' : paymentMethod;
  const isReturnExemptSalesOrg = isAustraliaSalesOrg || isUSSalesOrg || isJapanSalesOrg;
  return (
    <Sidebar
      title={
        <div className='ta-sm-l'>
          <TranslatedCopy id='cart.summary.title' />
        </div>
      }
      isOpen
      extraClassName='mt0-sm pb7-sm'
    >
      <div className='orderDetails__summary ta-sm-l pb4-sm u-full-width bg-white'>
        {accountName && <OrderDetailsSummaryBlock headerId='global.account' value={accountName} />}
        <OrderDetailsSummaryBlock headerId='global.department' value={toTitleCase(departmentName)} />
        <OrderDetailsSummaryBlock headerId='global.paymentMethod' value={methodOfPayment} />
        <OrderDetailsSummaryBlock headerId='global.orderType' value={ORDERTYPE_STRING_MAP[orderType]} />
        <OrderDetailsSummaryBlock headerId='orderstatus.poNumber' value={poNumber} />
        <OrderDetailsSummaryBlock
          headerId='orderstatus.orderSubmittedDate'
          value={<FormattedDate value={orderSubmittedDate} timeZone='utc' />}
        />
        <OrderDetailsSummaryBlock
          headerId='orderstatus.customerRequestDate'
          value={<FormattedDate value={customerRequestDate} timeZone='utc' />}
        />
        <OrderDetailsSummaryBlock headerId='global.submittedBy' value={orderSubmittedBy} />
      </div>
      {isForecast && (
        <div className='flex-container row start space-between'>
          <H2>
            <TranslatedCopy id='global.numberOfUnits' />
          </H2>
          <div className='body-3' data-desc='cart__sidebar_compTotal'>
            {totalQty}
          </div>
        </div>
      )}
      <div className='flex-container row center space-between border-bottom pt3-sm pb4-sm'>
        <H2>
          <TranslatedCopy id='orderstatus.netTotal' />
        </H2>
        {!isGreaterChinaSalesOrg && (
          <span className='body-3'>
            &nbsp;
            <Currency value={netWholeSaleTotal || '0'} type={currencyCode} />
          </span>
        )}
        {isGreaterChinaSalesOrg && (
          <span className='body-3 flex-container column'>
            <span>
              {' '}
              <Currency value={netWholeSaleTotal || '0'} type={currencyCode} />
            </span>
          </span>
        )}
      </div>
      {shipToAddress && !isForecast && (
        <div className='border-bottom pt5-sm pb5-sm ta-sm-l'>
          <H2 className='pt2-sm pb2-sm'>
            <TranslatedCopy id='global.shipping.info' />
          </H2>
          <div className='pb3-sm' data-desc='orderDetails_summary_shippingInfo'>
            <Address address={shipToAddress} />
          </div>
        </div>
      )}
      <div>
        {isLoadingSubmittedReturns && <Spinner small className='orderDetails__summary_spinner pb-4-sm' />}
        {hasReturns && (
          <div className='ta-sm-l u-full-width mt5-sm mb5-sm p2-sm pl4-sm body-4 bg-gameday-grey-two'>
            <Link to='/' extraClassName='text-color-secondary' onClick={handleRelatedReturnsClick}>
              <TranslatedCopy id='orderstatus.orderHasReturns' />
            </Link>
          </div>
        )}
        {!isForecast && !isGreaterChinaSalesOrg && (
          <>
            <Button
              extraClassName='ncss-btn-primary-dark mt6-sm full-width'
              disabled={
                !isEligibleForReturn || isExpiredContractReturn || isReturnExemptSalesOrg || isLoadingSubmittedReturns
              }
              data-desc='orderDetails_summary_shippingInfo_beginReturn'
              onClick={handleBeginReturnClick}
            >
              <TranslatedCopy id='global.beginReturn' />
            </Button>

            {!isReturnExemptSalesOrg && isEligibleForReturn && isExpiredContractReturn && !hasFutureContractReturn && (
              <p className='mt4-sm body-4 text-color-error'>
                <TranslatedCopy id='orderstatus.returnsContractMsg' />
              </p>
            )}

            {!isReturnExemptSalesOrg && isExpiredContractReturn && hasFutureContractReturn && (
              <p className='mt4-sm body-4 text-color-error'>
                <TranslatedCopy
                  id='orderstatus.returnsContract.future'
                  values={{ startDate: <FormattedDate value={futureReturnStartDate} timeZone='utc' /> }}
                />
              </p>
            )}
          </>
        )}
      </div>
    </Sidebar>
  );
};

OrderDetailsSummary.propTypes = {
  accountName: PropTypes.string.isRequired,
  departmentName: PropTypes.string.isRequired,
  paymentMethod: PropTypes.string.isRequired,
  orderType: PropTypes.string.isRequired,
  poNumber: PropTypes.string.isRequired,
  orderSubmittedDate: PropTypes.string.isRequired,
  orderSubmittedBy: PropTypes.string.isRequired,
  customerRequestDate: PropTypes.string.isRequired,
  netWholeSaleTotal: PropTypes.number.isRequired,
  currencyCode: PropTypes.string.isRequired,
  hasReturns: PropTypes.bool,
  handleRelatedReturnsClick: PropTypes.func.isRequired,
  isEligibleForReturn: PropTypes.bool.isRequired,
  isExpiredContractReturn: PropTypes.bool.isRequired,
  hasFutureContractReturn: PropTypes.bool.isRequired,
  futureReturnStartDate: PropTypes.string,
  handleBeginReturnClick: PropTypes.func.isRequired,
  shipToAddress: PropTypes.object,
  totalQty: PropTypes.number.isRequired,
  isForecast: PropTypes.bool.isRequired,
  isGreaterChinaSalesOrg: PropTypes.bool.isRequired,
  isUSSalesOrg: PropTypes.bool.isRequired,
  isAustraliaSalesOrg: PropTypes.bool.isRequired,
  isJapanSalesOrg: PropTypes.bool.isRequired,
  isLoadingSubmittedReturns: PropTypes.bool.isRequired
};

OrderDetailsSummary.defaultProps = {
  hasReturns: false,
  shipToAddress: {},
  futureReturnStartDate: null
};

const mapStateToProps = state => ({
  isLoadingSubmittedReturns: getPendingStatus(state, requestSubmittedReturns.event.name)
});

export default connect(mapStateToProps)(OrderDetailsSummary);
